import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link as RouterLink } from "react-router-dom";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { BrandLockup } from "../components/Brand";
import { ColorModeToggle } from "../components/ColorModeToggle";
import { loadMsg91Widget, WidgetError, type Msg91Widget } from "../auth/msg91Widget";
import { config } from "../config";
import { maskMobile, normalizeIndianMobile } from "../api/login";
import { optionalPasswordProblems } from "../api/register";
import { HumanCheck } from "../components/HumanCheck";
import { isTestMobile } from "../domain/testMobile";
import {
  acceptAvailability,
  acceptInvitation,
  checkUsername,
  roleLabels,
  sendInviteTestCode,
  testCodeToken,
  usernameProblem,
  verifyInvitation,
  USERNAME_RULE,
  type AcceptOutcome,
  type InvitationInfo,
  type VerifyInviteOutcome,
} from "../api/operatorLogins";
import { PasswordField } from "../components/PasswordField";

/** Seconds before the widget allows another send (MSG91 answered 704 before ~33 s). */
const RESEND_AFTER = 35;
const BUSY_RETRY = 60;
/** Wait this long after the last keystroke before asking whether a username is free. */
const USERNAME_DEBOUNCE_MS = 400;

export const DONE_TEXT =
  "Sign in at ankhos.io/signin with your username or mobile and your password. We will then send a code to your mobile.";

type Step = "mobile" | "code" | "details" | "done";

export interface DetailsForm {
  username: string;
  password: string;
  confirm: string;
}
const EMPTY_DETAILS: DetailsForm = { username: "", password: "", confirm: "" };

/** What the username field says under itself. */
export type UsernameState =
  | { kind: "idle" }
  | { kind: "problem"; text: string }
  | { kind: "checking" }
  | { kind: "available" }
  | { kind: "taken" }
  | { kind: "unknown" };

type Refusal =
  | Exclude<VerifyInviteOutcome, { kind: "verified" | "busy" }>
  | Exclude<AcceptOutcome, { kind: "created" | "incomplete" | "busy" | "username_taken" }>;

type Notice =
  | { kind: "problems"; problems: string[] }
  | { kind: "busy"; retryAfter: number }
  | { kind: "widget_error"; message: string }
  | { kind: "invalid_code" }
  | { kind: "invalid_mobile" }
  | { kind: "resent" }
  | { kind: "test_code" }
  | { kind: "outcome"; outcome: Refusal };

/** A request id from radaccess's test codes, not from MSG91. */
const isTestRequest = (id: string) => id.startsWith("test1.");

/** The details step's own refusals, before anything is sent. */
export function acceptFormProblems(f: DetailsForm): string[] {
  const p: string[] = [];
  const u = usernameProblem(f.username);
  if (u !== null) p.push(u);
  p.push(...optionalPasswordProblems(f.password, f.confirm));
  return p;
}

/** "Ajay Khosla, net.ankhnet invited you as Billing, Support." -- from the invitation, never typed. */
export function invitationLine(inv: InvitationInfo): string {
  const who = [inv.firstname, inv.lastname].filter(Boolean).join(" ");
  const by = inv.invitedby || inv.namespace || "An operator";
  const as = inv.kind === "suboperator" || inv.kind === "owner" ? `the owner of ${inv.namespace || "a site"}` : roleLabels(inv.roles) || "staff";
  return `${who ? `${who}, ` : ""}${by} invited you as ${as}.`;
}

function usernameHelper(s: UsernameState): string {
  switch (s.kind) {
    case "problem":
      return s.text;
    case "checking":
      return "Checking…";
    case "available":
      return "Available";
    case "taken":
      return "Already taken";
    default:
      return USERNAME_RULE;
  }
}

/**
 * /invite -- a person an operator invited (a member of its staff, or the owner
 * of one of its sub-operators) turns the invitation into a login (radaccess
 * v1.124, owner 2026-10-18). The SMS they received links to /signin, which
 * links here.
 *
 * Mobile first: the MSG91 code proves the SIM, and radaccess answers the
 * pending invitation with an accept ticket. Then the person chooses a
 * username (checked while typing) and a password. There is no link secret:
 * the DLT-registered SMS carries a static link, so proving the SIM is the
 * proof. The name, the operator and the roles come from the invitation the
 * operator wrote -- this page sends none of them.
 *
 * ⛔ No email here this release: an unproven address must never reach
 * ZITADEL, whose password reset mails it even unverified.
 *
 * ⛔ PUBLIC. No identity is read and no token is sent.
 */
export function AcceptInvite() {
  const widgetCfg = config().msg91;
  const [mobile, setMobile] = useState("");
  const [form, setForm] = useState<DetailsForm>(EMPTY_DETAILS);
  const [step, setStep] = useState<Step>("mobile");
  const [code, setCode] = useState("");
  const [reqId, setReqId] = useState("");
  const [masked, setMasked] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [acceptTicket, setAcceptTicket] = useState("");
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [userState, setUserState] = useState<UsernameState>({ kind: "idle" });
  const [done, setDone] = useState<{ roles: string[]; username: string } | null>(null);
  const [resendUntil, setResendUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const resendIn = Math.max(0, Math.ceil((resendUntil - now) / 1000));
  const widgetRef = useRef<Msg91Widget | null>(null);
  // A THROTTLED verify never looked at the token, so after "busy" the same
  // token is retried instead of asking for another code.
  const tokenRef = useRef<string>("");
  const codeRef = useRef<HTMLInputElement>(null);

  const setResendIn = (seconds: number) => {
    const t = Date.now();
    setNow(t);
    setResendUntil(t + seconds * 1000);
  };

  useEffect(() => {
    if (resendUntil <= now) return;
    const t = window.setTimeout(() => setNow(Date.now()), 1000);
    return () => window.clearTimeout(t);
  }, [resendUntil, now]);

  // Start the widget as the page opens: it fetches its own settings (the
  // captcha included) after it starts, and a send before they arrive fails.
  useEffect(() => {
    if (!widgetCfg) return;
    loadMsg91Widget(widgetCfg)
      .then((w) => {
        widgetRef.current = w;
      })
      .catch(() => undefined);
  }, [widgetCfg]);

  useEffect(() => {
    if (step === "code") codeRef.current?.focus();
  }, [step]);

  // Ask once, as the page opens, whether accepting can work at all -- so an
  // SMS code is never sent for an accept that cannot succeed. See
  // acceptAvailability for why this reveals nothing about any invitation.
  const [availability, setAvailability] = useState<"available" | "unavailable" | "unknown">("unknown");
  useEffect(() => {
    const ctl = new AbortController();
    void acceptAvailability(ctl.signal).then(setAvailability);
    return () => ctl.abort();
  }, []);
  const acceptDown = availability === "unavailable";

  // The username: the rule first, here; then, once it passes and typing has
  // paused, whether it is free. A newer keystroke cancels the older question.
  useEffect(() => {
    if (step !== "details" || !acceptTicket) return;
    const u = form.username;
    if (!u) {
      setUserState({ kind: "idle" });
      return;
    }
    const problem = usernameProblem(u);
    if (problem !== null) {
      setUserState({ kind: "problem", text: problem });
      return;
    }
    setUserState({ kind: "checking" });
    const ctl = new AbortController();
    const t = window.setTimeout(() => {
      checkUsername({ acceptTicket, username: u }, ctl.signal)
        .then((r) => {
          if (ctl.signal.aborted) return;
          if (r.kind === "available") setUserState({ kind: "available" });
          else if (r.kind === "taken") setUserState({ kind: "taken" });
          else if (r.kind === "invalid") setUserState({ kind: "problem", text: r.problem });
          else setUserState({ kind: "unknown" });
        })
        .catch(() => undefined);
    }, USERNAME_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(t);
      ctl.abort();
    };
  }, [form.username, acceptTicket, step]);

  const set = <K extends keyof DetailsForm>(k: K, v: DetailsForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  async function run(action: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  }

  const backToMobile = () => {
    tokenRef.current = "";
    setReqId("");
    setCode("");
    setAcceptTicket("");
    setInvitation(null);
    setForm(EMPTY_DETAILS);
    setStep("mobile");
  };

  const widgetNotice = (err: unknown): Notice =>
    err instanceof WidgetError && err.throttled
      ? { kind: "busy", retryAfter: BUSY_RETRY }
      : { kind: "widget_error", message: err instanceof WidgetError ? err.message : "The SMS service could not be reached" };

  const onMobileSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (!widgetCfg || acceptDown) return;
      const e164 = normalizeIndianMobile(mobile);
      if (e164 === null) {
        setNotice({ kind: "invalid_mobile" });
        return;
      }
      // A TEST number (test console only) cannot receive an SMS: radaccess
      // writes the code to the inviting operator's SMS log instead.
      if (isTestMobile(e164.slice(3))) {
        const t = await sendInviteTestCode(e164);
        if (t.kind === "sent") {
          tokenRef.current = "";
          setReqId(t.requestId);
          setMasked(maskMobile(e164));
          setResendIn(t.resendIn);
          setCode("");
          setStep("code");
          setNotice({ kind: "test_code" });
        } else if (t.kind === "busy") {
          setNotice({ kind: "busy", retryAfter: t.retryAfter });
        } else {
          setNotice({ kind: "widget_error", message: t.kind === "refused" ? t.message : "The code service could not be reached" });
        }
        return;
      }
      try {
        const widget = await loadMsg91Widget(widgetCfg);
        widgetRef.current = widget;
        const id = await widget.send(e164);
        tokenRef.current = "";
        setReqId(id);
        setMasked(maskMobile(e164));
        setResendIn(RESEND_AFTER);
        setCode("");
        setStep("code");
      } catch (err) {
        setNotice(widgetNotice(err));
      }
    });
  };

  const resend = () =>
    run(async () => {
      if (isTestRequest(reqId)) {
        const t = await sendInviteTestCode(mobile);
        if (t.kind === "sent") {
          tokenRef.current = "";
          setReqId(t.requestId);
          setResendIn(t.resendIn);
          setNotice({ kind: "test_code" });
        } else if (t.kind === "busy") {
          setNotice({ kind: "busy", retryAfter: t.retryAfter });
        } else {
          setNotice({ kind: "widget_error", message: t.kind === "refused" ? t.message : "The code service could not be reached" });
        }
        return;
      }
      const widget = widgetRef.current;
      if (!widget || !reqId) {
        backToMobile();
        return;
      }
      try {
        await widget.resend("sms", reqId);
        setResendIn(RESEND_AFTER);
        setNotice({ kind: "resent" });
      } catch (err) {
        setNotice(widgetNotice(err));
      }
    });

  const onCodeSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const widget = widgetRef.current;
      const testRequest = isTestRequest(reqId);
      if (!reqId || (!widget && !testRequest)) {
        backToMobile();
        return;
      }
      let token = tokenRef.current;
      if (!token && testRequest) {
        token = testCodeToken(reqId, code);
      } else if (!token && widget) {
        try {
          token = await widget.verify(code.trim(), reqId);
        } catch (err) {
          if (err instanceof WidgetError && err.throttled) {
            setNotice({ kind: "busy", retryAfter: BUSY_RETRY });
          } else {
            setNotice({ kind: "invalid_code" });
            setCode("");
          }
          return;
        }
        tokenRef.current = token;
      }

      const r = await verifyInvitation({ mobile, widgetToken: token });
      switch (r.kind) {
        case "verified":
          tokenRef.current = "";
          setAcceptTicket(r.acceptTicket);
          setInvitation(r.invitation);
          setForm(EMPTY_DETAILS);
          setStep("details");
          return;
        case "busy":
          setNotice({ kind: "busy", retryAfter: r.retryAfter });
          return;
        default:
          // A wrong test code spends nothing: stay and let it be typed again.
          if (testRequest && r.kind === "not_verified") {
            tokenRef.current = "";
            setNotice({ kind: "invalid_code" });
            setCode("");
            return;
          }
          // Every other answer spent the token.
          setNotice({ kind: "outcome", outcome: r });
          backToMobile();
          return;
      }
    });
  };

  const onDetailsSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const problems = acceptFormProblems(form);
      if (userState.kind === "taken") problems.unshift("That username is already taken.");
      if (problems.length > 0) {
        setNotice({ kind: "problems", problems });
        return;
      }
      const r = await acceptInvitation({ acceptTicket, username: form.username, password: form.password });
      switch (r.kind) {
        case "created":
          setDone({ roles: r.roles, username: r.username || form.username });
          setForm(EMPTY_DETAILS);
          setAcceptTicket("");
          setStep("done");
          return;
        case "username_taken":
          // The refused accept spent the old ticket; the 409 carries the next one.
          if (r.acceptTicket) setAcceptTicket(r.acceptTicket);
          setUserState({ kind: "taken" });
          setNotice({ kind: "problems", problems: ["That username was just taken. Choose another."] });
          return;
        case "busy":
          setNotice({ kind: "busy", retryAfter: r.retryAfter });
          return;
        case "incomplete":
          setNotice({ kind: "problems", problems: r.problems });
          return;
        case "retry":
          setNotice({ kind: "outcome", outcome: r });
          return;
        default:
          // The ticket is spent or gone: prove the mobile again.
          setNotice({ kind: "outcome", outcome: r });
          backToMobile();
          return;
      }
    });
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <BrandLockup to="/welcome" />
          <ColorModeToggle />
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Accept an invitation</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          An operator added you to AnkhOS, as its staff or as the owner of one of its sites. Confirm the mobile number they
          invited with a code, then choose a username and a password.
        </Typography>
        {!widgetCfg && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Mobile verification is not available right now</AlertTitle>
            Try again later.
          </Alert>
        )}
        {acceptDown && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Accepting invitations is not available right now</AlertTitle>
            No code has been sent. Try again later, or tell the operator who invited you.
          </Alert>
        )}
        {notice && <NoticeAlert notice={notice} />}

        {step === "mobile" && (
          <Paper variant="outlined" component="form" onSubmit={onMobileSubmit} sx={{ p: 3 }}>
            <Stack spacing={2}>
              <TextField
                label="Mobile number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                autoComplete="tel-national"
                helperText="The number the operator invited. We send it a code."
                required
              />
              {/* A test number's code comes from radaccess, which checks for a
                  human before writing it (MSG91's widget does its own check). */}
              {isTestMobile(mobile.replace(/\D/g, "").slice(-10)) && <HumanCheck action="invite" />}
              <Button type="submit" variant="contained" disabled={busy || !widgetCfg || acceptDown || !mobile.trim()}>Send code</Button>
            </Stack>
          </Paper>
        )}

        {step === "code" && (
          <Paper variant="outlined" component="form" onSubmit={onCodeSubmit} sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="body2">Enter the code sent to {masked}.</Typography>
              <TextField
                inputRef={codeRef}
                label="Code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                autoComplete="one-time-code"
                slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 8 } }}
                required
              />
              <HumanCheck action="invite" />
              <Stack direction="row" spacing={1}>
                <Button type="submit" variant="contained" disabled={busy || !code.trim()}>Verify</Button>
                <Button disabled={busy || resendIn > 0} onClick={() => void resend()}>
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </Button>
                <Button disabled={busy} onClick={backToMobile}>Back</Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        {step === "details" && (
          <AcceptDetailsView
            invitation={invitation}
            form={form}
            onChange={set}
            userState={userState}
            busy={busy}
            onSubmit={onDetailsSubmit}
            onBack={backToMobile}
          />
        )}

        {step === "done" && done && (
          <Alert severity="success" data-testid="accept-done">
            <AlertTitle>Invitation accepted{done.roles.length > 0 ? ` -- ${roleLabels(done.roles)}` : ""}</AlertTitle>
            {done.username && <>Your username is <strong>{done.username}</strong>. </>}
            {DONE_TEXT}
            <Box sx={{ mt: 1.5 }}>
              <Button variant="contained" size="small" component={RouterLink} to="/">Sign in</Button>
            </Box>
          </Alert>
        )}
      </Container>
    </Box>
  );
}

export interface AcceptDetailsViewProps {
  invitation: InvitationInfo | null;
  form: DetailsForm;
  onChange: <K extends keyof DetailsForm>(k: K, v: DetailsForm[K]) => void;
  userState: UsernameState;
  busy: boolean;
  onSubmit: (e: FormEvent) => void;
  onBack: () => void;
}

/** Step 2: who invited them, then username and password. */
export function AcceptDetailsView(p: AcceptDetailsViewProps) {
  const bad = p.userState.kind === "problem" || p.userState.kind === "taken";
  return (
    <Paper variant="outlined" component="form" onSubmit={p.onSubmit} sx={{ p: 3 }}>
      <Stack spacing={2}>
        {p.invitation && (
          <Alert severity="info" data-testid="invited-by">{invitationLine(p.invitation)}</Alert>
        )}
        <TextField
          label="Username"
          value={p.form.username}
          // Lower-cased as typed: the rule allows no capitals.
          onChange={(e) => p.onChange("username", e.target.value.toLowerCase())}
          autoComplete="username"
          error={bad}
          helperText={usernameHelper(p.userState)}
          slotProps={{ htmlInput: { autoCapitalize: "none", spellCheck: false, "data-testid": "username" } }}
          required
        />
        {p.userState.kind !== "idle" && (
          <Typography variant="caption" color="text.secondary">{USERNAME_RULE}</Typography>
        )}
        <PasswordField
          label="Password (optional)"
          value={p.form.password}
          onChange={(e) => p.onChange("password", e.target.value)}
          autoComplete="new-password"
          helperText={"Optional. Leave both blank to sign in with a code we text to your mobile; or 8+ characters, with upper and lower case, a number and a symbol."}
        />
        <PasswordField label="Confirm password" value={p.form.confirm} onChange={(e) => p.onChange("confirm", e.target.value)} autoComplete="new-password" />
        <HumanCheck action="invite" />
        <Stack direction="row" spacing={1}>
          <Button type="submit" variant="contained" disabled={p.busy || bad}>Accept invitation</Button>
          <Button disabled={p.busy} onClick={p.onBack}>Start again</Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function NoticeAlert({ notice }: { notice: Notice }) {
  switch (notice.kind) {
    case "problems":
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Please check these</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {notice.problems.map((p) => <li key={p}>{p}</li>)}
          </Box>
        </Alert>
      );
    case "busy":
      return <Alert severity="info" sx={{ mb: 2 }}>Too many attempts. Wait about {Math.ceil(notice.retryAfter / 60)} minute(s) and try again.</Alert>;
    case "widget_error":
      return <Alert severity="error" sx={{ mb: 2 }}>{notice.message}</Alert>;
    case "invalid_code":
      return <Alert severity="error" sx={{ mb: 2 }}>That code is not right. Check it and try again.</Alert>;
    case "invalid_mobile":
      return <Alert severity="error" sx={{ mb: 2 }}>Enter the 10-digit mobile number the operator invited.</Alert>;
    case "resent":
      return <Alert severity="info" sx={{ mb: 2 }}>A new code is on its way.</Alert>;
    case "test_code":
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Test number: no SMS is sent</AlertTitle>
          The operator who invited you can read the code in Reports › SMS log (status “TEST: not sent”).
        </Alert>
      );
    case "outcome":
      return <OutcomeAlert outcome={notice.outcome} />;
  }
}

function OutcomeAlert({ outcome }: { outcome: Refusal }) {
  switch (outcome.kind) {
    case "no_invitation":
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>No invitation for this number</AlertTitle>
          {outcome.message} It may have expired (invitations last 14 days) or been cancelled.
        </Alert>
      );
    case "not_verified":
      return <Alert severity="error" sx={{ mb: 2 }}>This mobile number was not verified. Send a new code and try again.</Alert>;
    case "ticket_expired":
      return <Alert severity="info" sx={{ mb: 2 }}>That took too long. Confirm your mobile number again.</Alert>;
    case "conflict":
      return <Alert severity="warning" sx={{ mb: 2 }}>{outcome.message}</Alert>;
    case "retry":
      return <Alert severity="warning" sx={{ mb: 2 }}>{outcome.message}</Alert>;
    case "unavailable":
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Accepting invitations is not available right now</AlertTitle>
          Nothing was changed. Try again later, or tell the operator who invited you.
        </Alert>
      );
  }
}
